// Sibling-tool switcher (item 255 of docs/PRODUCT-ROADMAP.md).
// GET/PATCH /api/admin/sibling-tools (Phase B, admin config) and
// GET /api/v1/tools (Phase C, wordmark switcher — every authenticated
// user). Mirrors handlers.SiblingToolDTO / tools.AvailableTool.

import useSWR from 'swr';

import { parseErrorResponse } from './problem';

export interface SiblingTool {
  key: string;
  enabled: boolean;
  base_url?: string;
  label?: string;
  updated_at?: string;
  updated_by?: string;
}

export interface AvailableTool {
  key: string;
  label: string;
  base_url: string;
}

export const TOOLS_KEY = '/api/v1/tools';

const fetcher = async (url: string) => {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json();
};

export function useSiblingTools() {
  return useSWR<SiblingTool[]>('/api/admin/sibling-tools', fetcher);
}

// Drives the wordmark switcher. Fails soft: an errored fetch (e.g. a
// degraded license returning an empty list is a normal 200, but a
// network error isn't) falls back to an empty list via onError, so
// the wordmark just renders unchanged rather than showing a broken nav.
export function useAvailableTools() {
  return useSWR<AvailableTool[]>(TOOLS_KEY, fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 30_000,
    onError: () => undefined,
  });
}

export async function patchSiblingTool(
  key: string,
  enabled: boolean,
  baseUrl: string,
  label: string,
): Promise<SiblingTool[]> {
  const res = await fetch('/api/admin/sibling-tools', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ key, enabled, base_url: baseUrl, label }),
  });
  if (!res.ok) throw await parseErrorResponse(res);
  return res.json();
}
